import React from 'react';
import {
  Stack,
  Text,
  Card,
  Group,
  Badge,
  Center,
  ThemeIcon,
} from '@mantine/core';
import { IconNotes } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import classes from './mobileJournal.module.css';
import { IJournalEntry } from '../../../../global/entities/Journal/Journal';

dayjs.extend(utc);

interface MobileJournalDayPanelProps {
  selectedDate: Date;
  entries: IJournalEntry[];
  onViewEntry: (entry: IJournalEntry) => void;
}

const getEntryCardClass = (entry: IJournalEntry): string => {
  if (entry.isCompleted) return classes.entryCardCompleted;
  if (entry.isDraft) return classes.entryCardDraft;
  return classes.entryCardDefault;
};

const getAnsweredCount = (entry: IJournalEntry): number => {
  return entry.responses.filter(
    (r) => !r.isSkipped && r.responseValue && r.responseValue.trim() !== '',
  ).length;
};

export const MobileJournalDayPanel: React.FC<MobileJournalDayPanelProps> = ({
  selectedDate,
  entries,
  onViewEntry,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="sm">
      {/* Date Header */}
      <Group justify="space-between" align="center">
        <Text size="sm" fw={600}>
          {dayjs(selectedDate).format('dddd, MMMM D')}
        </Text>
        {entries.length > 0 && (
          <Text size="xs" c="dimmed">
            {t('journal.entriesCount', { count: entries.length })}
          </Text>
        )}
      </Group>

      {/* Entry List */}
      <Stack gap="xs">
          {entries.length === 0 ? (
            <Center py="lg">
              <Stack align="center" gap="sm">
                <Text size="sm" c="dimmed">
                  {t('journal.noEntriesForDate', 'No entries for this date')}
                </Text>
              </Stack>
            </Center>
          ) : (
            <>
              {entries.map((entry) => (
                <Card
                  key={entry.id}
                  withBorder
                  p="sm"
                  className={`${classes.entryCard} ${getEntryCardClass(entry)}`}
                  onClick={() => onViewEntry(entry)}
                >
                  <Group justify="space-between" align="center">
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {dayjs.utc(entry.entryDate).local().format('HH:mm')}
                      </Text>
                      <Badge
                        size="xs"
                        color={
                          entry.isCompleted
                            ? 'green'
                            : entry.isDraft
                              ? 'yellow'
                              : 'gray'
                        }
                        variant="light"
                      >
                        {entry.isCompleted
                          ? t('journal.completed', 'Completed')
                          : t('journal.draft', 'Draft')}
                      </Badge>
                    </Group>
                    <Group gap="xs">
                      {entry.responses.length > 0 && (
                        <Text size="xs" c="dimmed">
                          {t(
                            'journal.answeredCount',
                            '{{answered}}/{{total}} questions',
                            {
                              answered: getAnsweredCount(entry),
                              total: entry.responses.length,
                            },
                          )}
                        </Text>
                      )}
                      {entry.notes && (
                        <ThemeIcon size="xs" variant="light" color="gray">
                          <IconNotes size={12} />
                        </ThemeIcon>
                      )}
                    </Group>
                  </Group>
                </Card>
              ))}
            </>
          )}
      </Stack>
    </Stack>
  );
};
